import axios from 'axios'
import { apiClient } from '@/api/client'
import type { DraftContent, DraftSummary, MenuContext, MenuItemDetail, OrderDraft, OrderQuote, PosMenu, PosOrderRequest, SubmittedOrder, PosPaymentInput, PosPayment, PosPaymentSummary, PosRefundInput, PosRefund, PosRefundSummary, PosRefundOutcome, PosCashStatus, PosCashOperation, PosCashClosing } from './types'

const root = (restaurantId: number) => `/api/restaurants/${restaurantId}`
const draftPath = (restaurantId: number, id: string) => `${root(restaurantId)}/order-drafts/${encodeURIComponent(id)}`
const posContent = (content: DraftContent) => ({ ...content, order: { ...content.order, source: 'pos' as const } })

export async function fetchPosMenu(restaurantId: number, context: MenuContext = {}, signal?: AbortSignal) {
  return (await apiClient.get<PosMenu>(`${root(restaurantId)}/pos-menu`, { params: context, signal })).data
}
export async function fetchPosMenuItem(restaurantId: number, itemId: number, context: MenuContext = {}, signal?: AbortSignal) {
  return (await apiClient.get<PosMenu<MenuItemDetail>>(`${root(restaurantId)}/pos-menu/items/${itemId}`, { params: context, signal })).data
}
export async function quotePosOrder(restaurantId: number, order: PosOrderRequest, signal?: AbortSignal) {
  return (await apiClient.post<OrderQuote>(`${root(restaurantId)}/order-quotes`, { ...order, source: 'pos' }, { signal })).data
}

/** Full POS draft quote, including delivery rules and fees. Existing item-only quote stays available. */
export async function quotePosDraft(restaurantId: number, content: DraftContent, signal?: AbortSignal) {
  return (await apiClient.post<OrderQuote>(`${root(restaurantId)}/order-drafts/quote`, { content: posContent(content) }, { signal })).data
}

/** Prepare once per new draft. Retry this same request, even if the editor has since changed. */
export function prepareDraftCreation(content: DraftContent) {
  return { content: structuredClone(posContent(content)), idempotencyKey: crypto.randomUUID() }
}
export async function createOrderDraft(restaurantId: number, request: ReturnType<typeof prepareDraftCreation>, signal?: AbortSignal) {
  return (await apiClient.post<OrderDraft>(`${root(restaurantId)}/order-drafts`, request, { signal })).data
}
export async function fetchOrderDraft(restaurantId: number, id: string, signal?: AbortSignal) {
  return (await apiClient.get<OrderDraft>(draftPath(restaurantId, id), { signal })).data
}
export async function fetchOrderDrafts(restaurantId: number, limit = 50, offset = 0, signal?: AbortSignal) {
  return (await apiClient.get<{ drafts: DraftSummary[] }>(`${root(restaurantId)}/order-drafts`, { params: { limit, offset }, signal })).data.drafts
}
export async function updateOrderDraft(restaurantId: number, id: string, revision: number, content: DraftContent, acceptMenuChanges = false, signal?: AbortSignal) {
  return (await apiClient.put<OrderDraft>(draftPath(restaurantId, id), { revision, content: posContent(content), acceptMenuChanges }, { signal })).data
}
export async function discardOrderDraft(restaurantId: number, id: string, revision: number, signal?: AbortSignal) {
  await apiClient.delete(draftPath(restaurantId, id), { params: { revision }, signal })
}
/** Retry with the same revision after an uncertain response; the backend returns the same order. */
export async function submitOrderDraft(restaurantId: number, id: string, revision: number, signal?: AbortSignal) {
  return (await apiClient.post<SubmittedOrder>(`${draftPath(restaurantId, id)}/submit`, { revision }, { signal })).data
}
/** Atomic order acceptance and tender recording. Never invokes or retries a card charge. */
export async function checkoutOrderDraft(restaurantId: number, id: string, revision: number, payment: PosPaymentInput) {
  return (await apiClient.post<SubmittedOrder>(`${draftPath(restaurantId, id)}/checkout`, { revision, payment })).data
}
export async function fetchPosPayment(restaurantId: number, orderId: number, signal?: AbortSignal) {
  return (await apiClient.get<PosPaymentSummary>(`${root(restaurantId)}/pos-orders/${orderId}/payment`, { signal })).data
}
export async function collectPosPayment(restaurantId: number, orderId: number, input: PosPaymentInput) {
  return (await apiClient.post<PosPayment>(`${root(restaurantId)}/pos-orders/${orderId}/payment`, input)).data
}
export async function fetchPosPayments(restaurantId: number, orderIds: number[], signal?: AbortSignal) {
  return (await apiClient.get<{ payments: PosPayment[] }>(`${root(restaurantId)}/pos-payments`, {
    params: { orderIds: orderIds.join(',') }, signal,
  })).data.payments
}

/** Keep cancellation separate from API/transport failures. No automatic write retries. */
export function posOrderingError(error: unknown): { code: string; status?: number; itemIndex?: number; cancelled: boolean } {
  if (axios.isCancel(error)) return { code: 'request_cancelled', cancelled: true }
  if (axios.isAxiosError<{ error?: string; itemIndex?: number | null } | null | undefined>(error)) {
    return { code: error.response?.data?.error ?? 'request_failed', status: error.response?.status,
      itemIndex: error.response?.data?.itemIndex ?? undefined, cancelled: false }
  }
  return { code: 'request_failed', cancelled: false }
}

export async function fetchPosRefunds(restaurantId: number, orderId: number, signal?: AbortSignal) {
  return (await apiClient.get<PosRefundSummary>(`${root(restaurantId)}/pos-orders/${orderId}/refunds`, { signal })).data
}
export async function preparePosRefund(restaurantId: number, orderId: number, input: PosRefundInput) {
  return (await apiClient.post<PosRefund>(`${root(restaurantId)}/pos-orders/${orderId}/refunds`, input)).data
}
export async function finishPosRefund(restaurantId: number, orderId: number, id: string, input: PosRefundOutcome) {
  return (await apiClient.post<PosRefund>(`${root(restaurantId)}/pos-orders/${orderId}/refunds/${encodeURIComponent(id)}/outcome`, input)).data
}
export async function cancelRefundedPosOrder(restaurantId: number, orderId: number) {
  return (await apiClient.post<{ cancelled: boolean }>(`${root(restaurantId)}/pos-orders/${orderId}/cancel-refunded`, {})).data
}

export async function fetchPosCash(restaurantId: number, signal?: AbortSignal) {
  return (await apiClient.get<PosCashStatus>(`${root(restaurantId)}/pos-cash`, { signal })).data
}
export async function executePosCash(restaurantId: number, input: PosCashOperation) {
  return (await apiClient.post<{ sessionId: string; kind: string }>(`${root(restaurantId)}/pos-cash/operations`, input)).data
}
export async function fetchPosCashReport(restaurantId: number, sessionId: string, signal?: AbortSignal) {
  return (await apiClient.get<PosCashClosing>(`${root(restaurantId)}/pos-cash/sessions/${encodeURIComponent(sessionId)}`, { signal })).data
}
